'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */

import classNames from 'classnames';
import { useId, useState } from 'react';
import { useCtaUrl } from '../../../components/ApplyCta';
import { AsideModal } from '../../../components/AsideModal';
import Button from '../../../components/Button';
import { MaterialIcon } from '../../../components/MaterialIcon';
import { Portal } from '../../../components/Portal/Portal';
import { RichText } from '../../../components/RichText';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../../utils/analytics/sendAnalytics';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { getClientTranslations } from '../../../utils/translations/getClientTranslations';
import {
  getRewardModuleGroups,
  giftTitleWithOverrides,
  RewardModuleTnc,
} from '../hotReward';
import { TRANSLATIONS } from '../i18n';
import { HotRewardCellData } from '../types';
import { RewardTierSections } from './RewardTierSections';
import { SectionLabel } from './SectionLabel';

interface HotRewardModuleProps {
  data: HotRewardCellData;
  /** Eyebrow copy; set by the trigger (CMS-1922 R4). Defaults to "🔥 All Offers". */
  title?: string;
  isOpen: boolean;
  onClose: () => void;
  pushDataLayer?: PushDataLayerFn;
  index: number;
  titleTableBuilder: string;
}

/**
 * One gift group: a label banner followed by its bulleted gift
 * titles. Returns a fragment so the banner and list become siblings of the
 * `section` flex container and pick up its uniform gap (Figma).
 */
const GiftGroup = ({ label, titles }: { label: string; titles: string[] }) => {
  if (titles.length === 0) return null;
  return (
    <>
      <SectionLabel>{label}</SectionLabel>
      <ul className="w-full list-disc ps-6 text-[16px] font-normal leading-[26px] text-text/primary">
        {titles.map((title, i) => (
          // eslint-disable-next-line react/no-array-index-key -- titles may repeat
          <li key={`${title}-${i}`} className="py-spacing/padding/soft-6px">
            {title}
          </li>
        ))}
      </ul>
    </>
  );
};

/**
 * Single collapsible "Terms & Conditions" accordion (Figma). Collapsed by
 * default; expands to reveal every offer's T&C, stacked (exclusive then
 * welcome). One stable id/aria-controls pair since there is only one accordion.
 */
const TncAccordion = ({
  label,
  items,
}: {
  label: string;
  items: RewardModuleTnc[];
}) => {
  const [open, setOpen] = useState(false);
  // Per-instance id: multiple Hot Reward cells each portal their own module to
  // <body>, so a hard-coded id would duplicate and break aria-controls.
  const contentId = useId();

  return (
    <div className="rounded-radius/button border border-borders/default bg-fill/alternative-1">
      <button
        type="button"
        className="flex w-full cursor-pointer items-start justify-between gap-3 p-spacing/padding/x-sm text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={contentId}
      >
        <span className="mhg-alpha-typography-table-cell-primary text-text/primary">
          {label}
        </span>
        <MaterialIcon
          icon="expand_less"
          className={`shrink-0 transition-transform ${open ? 'rotate-0' : 'rotate-180'}`}
        />
      </button>
      <div
        id={contentId}
        className={classNames(
          'flex flex-col gap-spacing/gap/soft-8px px-spacing/padding/x-sm pb-spacing/padding/x-sm',
          { hidden: !open },
        )}
      >
        {items.map((tnc) => (
          <RichText key={tnc.uid}>{tnc.description}</RichText>
        ))}
      </div>
    </div>
  );
};

/**
 * Expanded "See the full reward" module (CMS-1687).
 *
 * Titles-only, table-specific drawer on the existing AsideModal shell. Styled to
 * the Figma drawer: a title bar (🔥 eyebrow + product name), brand-tinted
 * Exclusive / Welcome gift-title banners with bulleted titles, a single
 * collapsible Terms & Conditions accordion, and a sticky Apply Now footer that
 * is a real `<a href>` clickout.
 */
export const HotRewardModule = ({
  data,
  title,
  isOpen,
  onClose,
  pushDataLayer,
  index,
  titleTableBuilder,
}: HotRewardModuleProps) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  const { product, rewardOffers, ctaOverrideUrl, ctaOverrideLabel, loanTerms } =
    data;
  // CTA label mirrors the row's apply action: the authored override (value-cell
  // link or identity-cell CTA override) wins, otherwise the default "Apply Now".
  const ctaLabel = ctaOverrideLabel || staticText.apply_now;
  // Forward the value-cell gift-title selector so its overrides apply to the
  // module's Exclusive / Welcome lists too (matched by gift title), keeping the
  // module titles consistent with the in-table cell.
  const groups = getRewardModuleGroups(rewardOffers, data.giftTitles);
  const titleOf = giftTitleWithOverrides(data.giftTitles);
  const productName = data.productName ?? product?.product_name ?? '';

  // CTA: the value-cell override wins, otherwise the product's clickout, with
  // the module's loan on a Personal Loan table (CMS-1921 R1). With no product
  // we fall back to the raw override.
  const resolvedCtaLink = useCtaUrl({
    product,
    ctaLinkOverride: ctaOverrideUrl,
    loanParams: loanTerms
      ? { amount: String(loanTerms.amount), tenure: String(loanTerms.tenure) }
      : undefined,
  });
  const ctaLink = product ? resolvedCtaLink : ctaOverrideUrl;

  const handleApplyClick = () => {
    if (!pushDataLayer || !product) return;
    sendProductAnalytics({
      action: UserActionEnum.CLICK,
      componentTitle: `${titleTableBuilder}/Hot Reward Module/CTA_${ctaLabel}`,
      componentType: ComponentTypeEnum.DRAWER,
      event: 'product_click_apply',
      index,
      product,
      pushDataLayer,
    });
  };

  return (
    // Portal to <body>: this module mounts inside the table's <td>, so without
    // it the fixed overlay is trapped in the table's stacking/containing-block
    // context and renders behind the header (z-index 100) and footer. Other
    // AsideModal usages mount at page level and don't need this (CMS-1687).
    <Portal>
      <AsideModal isOpen={isOpen} onClose={onClose} size="small">
        {/* White body (fill/card): the shared AsideModal panel defaults to the
            gray fill/alternative-1, but the Figma drawer body is white so the
            gray T&C box and tinted tags read against it. min-h-screen fills
            the full panel height (the AsideModal children wrapper has auto
            height, so a percentage min-height would collapse). */}
        <div className="flex min-h-screen flex-col bg-fill/card">
          {/* Title bar — pinned to the top of the scrolling panel. `pe-[40px]`
              reserves room for AsideModal's floating close button. */}
          <div className="sticky top-0 z-1 flex flex-col justify-center gap-spacing/gap/soft-12px border-b border-borders/default bg-fill/base px-spacing/padding/x py-spacing/padding/y-sm">
            <div className="flex w-full items-start gap-2 pe-[40px]">
              <div className="flex min-w-0 flex-1 flex-col gap-spacing/padding/soft-6px">
                <span className="mhg-alpha-typography-bullet-2 text-fill/primary">
                  {title ?? staticText.module_all_offers}
                </span>
                {productName && (
                  <span className="font-semibold text-font-size/subheading-2 leading-line-height/subheading-2 text-text/primary">
                    {productName}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Offer groups: by tier at the module's loan terms in Personal Loan
              mode (CMS-1922 R4), a flat list of titles everywhere else. */}
          <div className="flex flex-col items-start gap-spacing/padding/x-sm px-spacing/padding/x py-spacing/padding/section-x">
            {loanTerms ? (
              <>
                <RewardTierSections
                  label={staticText.module_pl_exclusive}
                  offers={rewardOffers?.exclusiveOffers ?? []}
                  loanTerms={loanTerms}
                  titleOf={titleOf}
                  staticText={staticText}
                  currency={context.currencyDisplay}
                />
                <RewardTierSections
                  label={staticText.module_pl_provider}
                  offers={rewardOffers?.welcomeOffers ?? []}
                  loanTerms={loanTerms}
                  titleOf={titleOf}
                  staticText={staticText}
                  currency={context.currencyDisplay}
                />
              </>
            ) : (
              <>
                <GiftGroup
                  label={staticText.module_exclusive}
                  titles={groups.exclusive}
                />
                <GiftGroup
                  label={staticText.module_welcome}
                  titles={groups.welcome}
                />
              </>
            )}
          </div>

          {/* Terms & Conditions */}
          {groups.tnc.length > 0 && (
            <div className="px-spacing/margin/x pb-spacing/gap/section-y">
              <TncAccordion label={staticText.module_tnc} items={groups.tnc} />
            </div>
          )}

          {/* Clearance so the last content clears the fixed CTA footer. */}
          <div aria-hidden className="h-30 shrink-0" />

          {ctaLink && (
            <div className="fixed bottom-0 right-0 z-10 w-full bg-fill/card shadow-[0px_-2px_8px_0px_rgba(0,0,0,0.08)] md:w-[640px] lg:w-[720px]">
              <div className="px-spacing/padding/x py-spacing/margin/y">
                <Button
                  as="a"
                  href={ctaLink}
                  onClick={handleApplyClick}
                  styleType="cta"
                  className="w-full bg-brand-cta"
                >
                  {ctaLabel}
                </Button>
              </div>
            </div>
          )}
        </div>
      </AsideModal>
    </Portal>
  );
};
